/**
 * CustomPromptModal Component
 * Elegant modal for custom AI prompts
 *
 * Features:
 * - Clean, minimalist design
 * - Auto-focus on textarea
 * - Enter to submit (Shift+Enter for newline)
 * - Escape to close
 * - Click outside to close
 * - File attachments (images, PDFs, documents) via fileUploadService
 */
import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, Sparkles, Loader2, Paperclip, User2, MessageCircle, Wand2, Lightbulb } from 'lucide-react';
import { useTheme } from '../../../contexts/ThemeContext';
import { getWorkspaceColors } from '../styles';
import fileUploadService from '../../../services/global/fileUploadService';
import AttachedFilesPreview from '../AttachedFilesPreview';

const CONTEXT_SUGGESTIONS = [
  'Gere uma resposta bonita para essas perguntas do cliente',
  'Reformule minhas mensagens em um tom mais profissional',
  'Resuma o que conversamos até aqui',
  'Crie uma proposta com base nessa conversa',
];

const truncate = (s, n = 90) => {
  const text = (s || '').trim().replace(/\s+/g, ' ');
  return text.length > n ? `${text.slice(0, n)}…` : text;
};

const CustomPromptModal = ({
  isOpen,
  onClose,
  onSubmit,
  isProcessing = false,
  selectedMessagesPreview = [],
}) => {
  const hasContext = Array.isArray(selectedMessagesPreview) && selectedMessagesPreview.length > 0;
  const { colors, theme } = useTheme();
  const workspaceColors = getWorkspaceColors(colors, theme === 'dark');
  const [prompt, setPrompt] = useState('');
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');

  const textareaRef = useRef(null);
  const modalRef = useRef(null);
  const fileInputRef = useRef(null);

  // Focus textarea when modal opens
  useEffect(() => {
    if (isOpen && textareaRef.current) {
      setTimeout(() => textareaRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setPrompt('');
      setAttachedFiles([]);
      setUploadError('');
      setIsUploading(false);
    }
  }, [isOpen]);

  const busy = isProcessing || isUploading;

  // Handle escape key
  useEffect(() => {
    if (!isOpen || busy) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose?.();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose, busy]);

  const handleAttachClick = () => {
    if (busy) return;
    fileInputRef.current?.click();
  };

  const handleFilesSelected = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setIsUploading(true);
    setUploadError('');

    try {
      const { uploaded, errors } = await fileUploadService.uploadMultiple(files);
      if (uploaded.length > 0) {
        setAttachedFiles((prev) => [...prev, ...uploaded]);
      }
      if (errors.length > 0) {
        setUploadError(errors.map((er) => `${er.filename}: ${er.error}`).join(' | '));
      }
    } catch (err) {
      setUploadError(err.message || 'Erro ao enviar arquivos');
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleRemoveFile = (index) => {
    setAttachedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleClearAllFiles = () => {
    setAttachedFiles([]);
  };

  // Handle click outside
  const handleBackdropClick = (e) => {
    if (busy) return;
    if (modalRef.current && !modalRef.current.contains(e.target)) {
      onClose?.();
    }
  };

  // Handle submit
  const handleSubmit = () => {
    if (!prompt.trim() || busy) return;
    onSubmit?.({ prompt: prompt.trim(), files: attachedFiles });
  };

  // Handle key press in textarea
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  if (!isOpen) return null;

  const accentPrimary = workspaceColors.accentPrimary;
  const canSubmit = prompt.trim() && !busy;

  const styles = {
    backdrop: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.6)',
      backdropFilter: 'blur(4px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10000,
      padding: 20,
    },
    modal: {
      backgroundColor: workspaceColors.bgPrimary,
      borderRadius: 16,
      width: '100%',
      maxWidth: 520,
      boxShadow: '0 20px 60px rgba(0, 0, 0, 0.4)',
      borderWidth: 1,
      borderStyle: 'solid',
      borderColor: workspaceColors.borderColor,
      overflow: 'hidden',
      animation: 'modalSlideIn 0.2s ease-out',
      display: 'flex',
      flexDirection: 'column',
      maxHeight: '90vh',
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '16px 20px',
      borderBottom: `1px solid ${workspaceColors.borderColor}`,
    },
    headerLeft: {
      display: 'flex',
      alignItems: 'center',
      gap: 12,
    },
    headerIcon: {
      width: 40,
      height: 40,
      borderRadius: 10,
      backgroundColor: `${accentPrimary}20`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: accentPrimary,
    },
    headerTitle: {
      fontSize: 16,
      fontWeight: 600,
      color: workspaceColors.textPrimary,
      margin: 0,
    },
    headerSubtitle: {
      fontSize: 12,
      color: workspaceColors.textSecondary,
      margin: 0,
      marginTop: 2,
    },
    closeBtn: {
      width: 36,
      height: 36,
      border: 'none',
      background: 'transparent',
      color: workspaceColors.textTertiary,
      cursor: busy ? 'not-allowed' : 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: 8,
      transition: 'all 0.15s ease',
      opacity: busy ? 0.5 : 1,
    },
    body: {
      padding: 20,
      overflowY: 'auto',
    },
    textareaWrapper: {
      backgroundColor: workspaceColors.bgSecondary,
      borderRadius: 12,
      borderWidth: 1,
      borderStyle: 'solid',
      borderColor: workspaceColors.borderColor,
      overflow: 'hidden',
      transition: 'border-color 0.15s ease',
      position: 'relative',
    },
    textarea: {
      width: '100%',
      minHeight: 120,
      maxHeight: 240,
      padding: '16px 16px 48px 16px',
      border: 'none',
      background: 'transparent',
      color: workspaceColors.textPrimary,
      fontSize: 14,
      lineHeight: 1.6,
      resize: 'vertical',
      outline: 'none',
      fontFamily: 'inherit',
      boxSizing: 'border-box',
    },
    toolsRow: {
      position: 'absolute',
      bottom: 8,
      left: 8,
      right: 8,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      pointerEvents: 'none',
    },
    toolsLeft: {
      display: 'flex',
      alignItems: 'center',
      gap: 6,
      pointerEvents: 'auto',
    },
    hint: {
      fontSize: 11,
      color: workspaceColors.textTertiary,
      marginTop: 10,
      display: 'flex',
      alignItems: 'center',
      gap: 6,
    },
    errorHint: {
      fontSize: 12,
      color: workspaceColors.danger || '#EF4444',
      marginTop: 8,
    },
    footer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      gap: 12,
      padding: '16px 20px',
      borderTop: `1px solid ${workspaceColors.borderColor}`,
      backgroundColor: workspaceColors.bgTertiary,
    },
    cancelBtn: {
      padding: '10px 20px',
      borderRadius: 8,
      border: 'none',
      backgroundColor: 'transparent',
      color: workspaceColors.textSecondary,
      fontSize: 14,
      fontWeight: 500,
      cursor: busy ? 'not-allowed' : 'pointer',
      transition: 'all 0.15s ease',
      opacity: busy ? 0.5 : 1,
    },
    submitBtn: {
      padding: '10px 24px',
      borderRadius: 8,
      border: 'none',
      backgroundColor: canSubmit ? accentPrimary : workspaceColors.bgSecondary,
      color: canSubmit ? 'white' : workspaceColors.textTertiary,
      fontSize: 14,
      fontWeight: 600,
      cursor: canSubmit ? 'pointer' : 'not-allowed',
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      transition: 'all 0.15s ease',
    },
    spinner: {
      animation: 'spin 1s linear infinite',
    },
    contextSection: {
      padding: '12px 20px',
      borderBottom: `1px solid ${workspaceColors.borderColor}`,
      backgroundColor: workspaceColors.bgTertiary,
    },
    contextHeader: {
      display: 'flex',
      alignItems: 'center',
      gap: 6,
      fontSize: 12,
      fontWeight: 600,
      color: workspaceColors.textSecondary,
      marginBottom: 8,
      textTransform: 'uppercase',
      letterSpacing: 0.4,
    },
    contextList: {
      display: 'flex',
      flexDirection: 'column',
      gap: 6,
      maxHeight: 140,
      overflowY: 'auto',
    },
    contextItem: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: 8,
      padding: '6px 8px',
      borderRadius: 8,
      backgroundColor: workspaceColors.bgPrimary,
    },
    contextRolePill: (role) => ({
      display: 'inline-flex',
      alignItems: 'center',
      gap: 3,
      padding: '2px 6px',
      borderRadius: 999,
      backgroundColor: role === 'operator' ? workspaceColors.roleOperator : workspaceColors.roleCustomer,
      color: '#fff',
      fontSize: 10,
      fontWeight: 700,
      letterSpacing: 0.3,
      whiteSpace: 'nowrap',
      flexShrink: 0,
      marginTop: 1,
    }),
    contextItemText: {
      flex: 1,
      fontSize: 12,
      lineHeight: 1.4,
      color: workspaceColors.textPrimary,
      wordBreak: 'break-word',
    },
    suggestionsSection: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: 6,
      marginBottom: 12,
    },
    suggestionChip: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 4,
      padding: '6px 10px',
      borderRadius: 999,
      border: `1px solid ${workspaceColors.borderColor}`,
      backgroundColor: 'transparent',
      color: workspaceColors.textSecondary,
      fontSize: 11,
      fontWeight: 500,
      cursor: 'pointer',
      transition: 'all 0.15s ease',
    },
  };

  const modalContent = (
    <div style={styles.backdrop} onClick={handleBackdropClick}>
      <div ref={modalRef} style={styles.modal}>
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.headerLeft}>
            <div style={styles.headerIcon}>
              <Sparkles size={20} />
            </div>
            <div>
              <h3 style={styles.headerTitle}>Chamar o Agente</h3>
              <p style={styles.headerSubtitle}>
                {hasContext
                  ? `${selectedMessagesPreview.length} mensagem${selectedMessagesPreview.length !== 1 ? 's' : ''} selecionada${selectedMessagesPreview.length !== 1 ? 's' : ''} como contexto`
                  : 'Escreva, grave sua voz ou anexe arquivos'}
              </p>
            </div>
          </div>
          <button
            style={styles.closeBtn}
            onClick={onClose}
            disabled={busy}
          >
            <X size={20} />
          </button>
        </div>

        {/* Attached Files Preview */}
        <AttachedFilesPreview
          files={attachedFiles}
          onRemoveFile={handleRemoveFile}
          onClearAll={handleClearAllFiles}
        />

        {/* Context (selected chat messages) */}
        {hasContext && (
          <div style={styles.contextSection}>
            <div style={styles.contextHeader}>
              <Paperclip size={12} />
              Contexto: {selectedMessagesPreview.length} mensagem{selectedMessagesPreview.length !== 1 ? 's' : ''} selecionada{selectedMessagesPreview.length !== 1 ? 's' : ''}
            </div>
            <div style={styles.contextList}>
              {selectedMessagesPreview.map((m, i) => {
                const isOperator = m.role === 'operator';
                const RoleIcon = isOperator ? User2 : MessageCircle;
                const roleLabel = isOperator ? 'Você' : (m.senderLabel || 'Cliente');
                const text = m.text || (m.type && m.type !== 'text' ? `[${m.type}]` : '');
                return (
                  <div key={`ctx-${i}`} style={styles.contextItem}>
                    <span style={styles.contextRolePill(m.role)}>
                      <RoleIcon size={10} strokeWidth={2.5} />
                      {roleLabel}
                    </span>
                    <span style={styles.contextItemText}>{truncate(text, 110)}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Body */}
        <div style={styles.body}>
          {hasContext && (
            <div style={styles.suggestionsSection}>
              {CONTEXT_SUGGESTIONS.map((s) => (
                <button
                  key={s}
                  type="button"
                  style={styles.suggestionChip}
                  onClick={() => {
                    setPrompt(s);
                    setTimeout(() => textareaRef.current?.focus(), 0);
                  }}
                  disabled={busy}
                  onMouseEnter={(e) => {
                    if (!busy) {
                      e.currentTarget.style.borderColor = workspaceColors.accentPrimary;
                      e.currentTarget.style.color = workspaceColors.accentPrimary;
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = workspaceColors.borderColor;
                    e.currentTarget.style.color = workspaceColors.textSecondary;
                  }}
                >
                  <Wand2 size={11} />
                  {s}
                </button>
              ))}
            </div>
          )}
          <div style={styles.textareaWrapper}>
            <textarea
              ref={textareaRef}
              style={styles.textarea}
              placeholder={hasContext
                ? 'Ex: Gere uma resposta bonita para essas perguntas do cliente, ou peça outra coisa…'
                : 'Ex: Resuma o conteúdo do PDF anexado, Crie uma proposta comercial, Analise os dados...'}
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={busy}
            />

            <div style={styles.toolsRow}>
              <div style={styles.toolsLeft}>
                {/* Attach button (mesmo padrao do "Anexar arquivo" no agendar mensagem) */}
                <button
                  type="button"
                  onClick={handleAttachClick}
                  disabled={busy}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 4,
                    background: 'none', border: 'none', cursor: busy ? 'not-allowed' : 'pointer',
                    color: workspaceColors.accentPrimary, fontSize: 12, padding: '2px 6px',
                    borderRadius: 4, opacity: busy ? 0.5 : 1,
                  }}
                >
                  {isUploading ? (
                    <Loader2 size={13} style={styles.spinner} />
                  ) : (
                    <Paperclip size={13} />
                  )}
                  {isUploading ? 'Enviando...' : 'Anexar arquivo'}
                </button>
              </div>
            </div>
          </div>

          {uploadError && <div style={styles.errorHint}>{uploadError}</div>}

          <div style={styles.hint}>
            <Lightbulb size={14} />
            <span>Enter para enviar • Shift+Enter para nova linha • 25MB por arquivo</span>
          </div>

          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={fileUploadService.getAcceptString()}
            onChange={handleFilesSelected}
            style={{ display: 'none' }}
          />
        </div>

        {/* Footer */}
        <div style={styles.footer}>
          <button
            style={styles.cancelBtn}
            onClick={onClose}
            disabled={busy}
          >
            Cancelar
          </button>
          <button
            style={styles.submitBtn}
            onClick={handleSubmit}
            disabled={!canSubmit}
          >
            {isProcessing ? (
              <>
                <Loader2 size={18} style={styles.spinner} />
                Gerando...
              </>
            ) : (
              <>
                <Sparkles size={18} />
                Gerar
              </>
            )}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes modalSlideIn {
          from {
            opacity: 0;
            transform: scale(0.95) translateY(-10px);
          }
          to {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.3; }
        }
      `}</style>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default CustomPromptModal;
